import { useState } from "react";
import { CheckCircle2, MessageSquare, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ContactForm } from "@/components/calculator/ContactForm";
import { api, fieldErrorsFrom, messageFrom } from "@/lib/api";
import { trackContactRequest } from "@/lib/analytics";

/**
 * The single "not catered for" path. Everything that can't be priced
 * automatically — ceilings, more than two years, a Trust, a going-concern flag,
 * any stop-and-check answer — arrives here, worded gently.
 */
export const FallbackPanel = ({
  headline,
  body,
  reasons = [],
  sessionId,
  service,
  answers,
  onBack,
}) => {
  const [serverErrors, setServerErrors] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(null);

  const submit = async (values) => {
    setBusy(true);
    setServerErrors(null);
    try {
      const data = await api.submitFallback({
        ...values,
        session_id: sessionId || null,
        service: service || null,
        answers: answers || null,
        reasons,
      });
      setReference(data.reference);
      trackContactRequest(service, data.reference);
      toast.success("Thank you — your details are with the T-TITE team.");
    } catch (error) {
      const fieldErrors = fieldErrorsFrom(error);
      if (fieldErrors) setServerErrors(fieldErrors);
      else toast.error(messageFrom(error));
    } finally {
      setBusy(false);
    }
  };

  if (reference) {
    return (
      <div data-testid="fallback-success" className="tt-card tt-fade-in p-6 sm:p-8">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--tt-teal-mist)] text-primary">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </span>
        <h2 className="mt-4 text-2xl font-bold text-foreground">Thank you — we're on it</h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Your details are with our team and someone will be in touch to talk this through and put
          a proper quote together for you.
        </p>
        <p className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-[color:var(--tt-surface-2)] px-4 py-2.5 text-sm">
          <span className="text-xs font-bold uppercase tracking-[0.1em] text-primary">
            Your reference
          </span>
          <span data-testid="fallback-reference" className="font-amount font-bold text-foreground">
            {reference}
          </span>
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button asChild variant="outline" className="bg-white">
            <a href="tel:+27817882993">
              <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
              Call us on 081 788 2993
            </a>
          </Button>
          {onBack ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              data-testid="fallback-restart-button"
              className="text-primary"
            >
              Back to the calculator
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="fallback-panel" className="tt-fade-in space-y-5">
      <div className="tt-card overflow-hidden">
        <div className="border-b-2 border-primary bg-[color:var(--tt-teal-mist)] px-5 py-6 sm:px-7">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-white text-primary">
            <MessageSquare className="h-5 w-5" aria-hidden="true" />
          </span>
          <h2
            data-testid="fallback-headline"
            className="mt-4 text-2xl font-bold leading-tight text-foreground sm:text-[28px]"
          >
            {headline || "Let's talk this one through"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[color:var(--tt-teal-deep)]">
            {body ||
              "We'd like to go through this with you directly rather than generate an automatic quote — this helps us make sure we scope things properly for your business."}
          </p>
        </div>

        {reasons.length ? (
          <div className="border-b border-border px-5 py-5 sm:px-7">
            <h3 className="tt-eyebrow">Why we'd rather speak to you</h3>
            <ul data-testid="fallback-reasons" className="mt-3 space-y-2.5">
              {reasons.map((reason, index) => (
                <li
                  key={reason.code ? `${reason.code}-${index}` : index}
                  data-testid="fallback-reason"
                  className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground"
                >
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                    aria-hidden="true"
                  />
                  {reason.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="px-5 py-6 sm:px-7">
          <h3 className="text-base font-bold text-foreground">
            Leave your details and we'll come back to you
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            No automatic number — a real person will look at this and quote you properly.
          </p>
          <div className="mt-5">
            <ContactForm
              variant="fallback"
              idPrefix="fallback"
              busy={busy}
              serverErrors={serverErrors}
              onSubmit={submit}
            />
          </div>
        </div>
      </div>

      {onBack ? (
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          data-testid="fallback-back-button"
          className="bg-white"
        >
          Back to the calculator
        </Button>
      ) : null}
    </div>
  );
};
